// hooks/market/useShop.ts
//
// Récupère le détail d'une boutique par son slug, y compris son
// ui_config (pour ThemeProvider) et ses infos de contact (pour
// ShopHeader). Appelle le vrai backend :
//   POST {API_BASE_URL}/market   body: { action: 'get-shop', access_token, slug }
// (voir backend/handleMarket.ts → getShop)

import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';
import type { ShopUiConfig } from '@/lib/market/uiConfigDefaults';
import type { MarketShopDetail } from '@/components/market/ShopHeader';

// Le détail complet ajoute ui_config au type utilisé par ShopHeader.
export type MarketShopFull = MarketShopDetail & {
  ui_config: ShopUiConfig;
};

export function useShop(slug: string | undefined) {
  const [shop, setShop] = useState<MarketShopFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const fetchShop = useCallback(async () => {
    if (!slug) return;

    setLoading(true);
    setError(null);
    setNotFound(false);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'get-shop',
          access_token: token,
          slug,
        }),
      });

      const data = await res.json();

      if (res.status === 404) {
        setNotFound(true);
        return;
      }

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de charger la boutique');
        return;
      }

      setShop(data.shop);
    } catch (err) {
      console.error('[useShop]', err);
      setError('network_error');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchShop();
  }, [fetchShop]);

  return { shop, loading, error, notFound, refresh: fetchShop };
}
